import { useState, useEffect, useRef } from "react";
import { Check, X, CaretDown } from "@phosphor-icons/react";

// Phase 2A — Multi-user picker. Shows chips for selected users, dropdown for picking more.
// Value: array of user_ids. onChange returns the updated array.
export default function TaskMultiAssignSelect({ users, value, onChange, currentUserId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selected = Array.isArray(value) ? value : [];
  const byId = Object.fromEntries(users.map((u) => [u.user_id, u]));
  const toggle = (uid) => {
    if (selected.includes(uid)) onChange(selected.filter((x) => x !== uid));
    else onChange([...selected, uid]);
  };
  const remove = (uid) => onChange(selected.filter((x) => x !== uid));

  return (
    <div ref={ref} className="relative" data-testid="task-multi-assign-wrap">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="task-multi-assign-toggle"
        className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none text-left inline-flex items-center justify-between gap-2 hover:border-[#002FA7]"
      >
        <span className="text-sm text-slate-600">
          {selected.length === 0 ? "Select one or more users…" : `${selected.length} user${selected.length === 1 ? "" : "s"} selected`}
        </span>
        <CaretDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2" data-testid="task-multi-assign-chips">
          {selected.map((uid) => {
            const u = byId[uid];
            if (!u) return null;
            return (
              <span key={uid} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2 py-1 border border-slate-300 bg-slate-50 font-bold">
                {u.name} <span className="text-slate-400">· {(u.role || "").toUpperCase()}</span>
                <button type="button" onClick={() => remove(uid)} className="ml-1 hover:text-red-600" aria-label={`Remove ${u.name}`}>
                  <X size={10} weight="bold" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      {open && (
        <div
          data-testid="task-multi-assign-dropdown"
          className="absolute z-30 top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto border border-slate-300 bg-white shadow-lg"
        >
          {users.length === 0 && (
            <div className="px-3 py-2 text-sm text-slate-400 italic">No users available</div>
          )}
          {users.map((u) => {
            const isSel = selected.includes(u.user_id);
            const isMe = u.user_id === currentUserId;
            return (
              <button
                type="button"
                key={u.user_id}
                onClick={() => toggle(u.user_id)}
                data-testid={`task-multi-assign-option-${u.user_id}`}
                className={`w-full px-3 py-2 text-sm flex items-center justify-between hover:bg-slate-50 ${isSel ? "bg-[#002FA7]/5" : ""}`}
              >
                <span className="truncate">
                  {u.name} <span className="text-slate-400">· {(u.role || "").toUpperCase()}</span>
                  {isMe && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#002FA7] font-bold">YOU</span>}
                </span>
                {isSel && <Check size={14} weight="bold" className="text-[#10B981]" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
